// src/components/PortfolioModal.js
import React, { useState, useEffect } from 'react';
import './PortfolioModal.css'; // Import specific styles

// --- IMPORTANT NOTE ---
// Directly fetching/scraping data from Naver Blog (or any external site)
// from the client-side (React) is generally NOT recommended or reliable due to:
// 1. CORS (Cross-Origin Resource Sharing) policies set by Naver, blocking requests.
// 2. Naver's Terms of Service might prohibit automated scraping.
// 3. Naver's website structure can change, breaking the scraper.
//
// The CORRECT approach involves a backend server or serverless function (like AWS Lambda,
// Google Cloud Functions, or even Google Apps Script) that fetches the data from the blog
// and provides it to your React app via an API endpoint.
//
// This component uses MOCK DATA for demonstration purposes.
// Replace the mock data fetching logic with a real API call to your backend if implemented.

const MOCK_POSTS = [
  {
    id: 1,
    title: "오산시 아파트 시스템 창호 외부 유리창 청소",
    link: "https://blog.naver.com/viewtylab/", // Replace with actual post links
    date: "2025년 4월 26일",
    excerpt: "최근 오산시에 위치한 고객님 댁의 시스템 창호 외부 유리창을 깨끗하게 청소해드렸습니다. 뿌옇던 창문이 맑아져 집안 분위기가 훨씬 밝아졌다고 만족해하셨습니다..."
  },
  {
    id: 2,
    title: "동탄 신도시 상가 통유리창 정기 관리",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 4월 20일",
    excerpt: "동탄 신도시에 위치한 상가의 통유리창 정기 관리를 진행했습니다. 매장 외부 이미지가 중요한 만큼, 항상 최상의 투명도를 유지하도록 꼼꼼하게 작업합니다."
  },
  {
    id: 3,
    title: "빌라 복층 구조 높은 창문 청소 후기",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 4월 15일",
    excerpt: "청소가 까다로운 빌라 복층 구조의 높은 창문도 안전하고 깨끗하게 청소 가능합니다. 전용 장비와 노하우로 손이 닿지 않는 곳까지 완벽하게 마무리합니다..."
  },
  {
    id: 4,
    title: "비 온 뒤 창문 얼룩 제거 전문 케어",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 4월 10일",
    excerpt: "장마철이나 비가 온 뒤에 남는 창문 얼룩, 맑은뷰연구소의 전문 케어로 해결하세요. 특수 약품과 기술로 물때와 먼지 얼룩을 말끔하게 제거합니다."
  },
  {
    id: 5,
    title: "강남구 20층 오피스 빌딩 외벽 로프 작업",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 4월 5일",
    excerpt: "고층 빌딩의 외벽 유리는 정기적인 관리가 필수입니다. 전문 로프공 팀이 투입되어 안전하고 완벽하게 외벽 오염을 제거했습니다."
  },
  {
    id: 6,
    title: "수원시 초등학교 교실 및 복도 창문 대청소",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 3월 28일",
    excerpt: "아이들의 쾌적한 학습 환경을 위해 학교 전체 창문 청소를 진행했습니다. 교실 안팎의 묵은 먼지를 제거하여 밝은 교실을 만들어드렸습니다."
  },
  {
    id: 7,
    title: "판교 신축 아파트 입주 전 윈도우 클리닝",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 3월 20일",
    excerpt: "입주를 앞둔 고객님을 위해 공사 분진과 시멘트 자국을 말끔히 제거했습니다. 새 집의 투명한 뷰를 완벽하게 찾아드렸습니다."
  },
  {
    id: 8,
    title: "성수동 카페 내부 유리 파티션 및 쇼윈도 관리",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 3월 15일",
    excerpt: "인테리어가 돋보이는 카페의 유리 파티션과 쇼윈도를 관리해드렸습니다. 지문과 얼룩 없이 투명함을 유지하여 매장 분위기를 한층 업그레이드했습니다."
  },
  {
    id: 9,
    title: "분당구 상가 건물 정기 관리 계약 및 첫 작업",
    link: "https://blog.naver.com/viewtylab/",
    date: "2025년 3월 10일",
    excerpt: "상가 건물의 가치를 높이는 정기 관리 서비스를 시작했습니다. 공용부 유리창과 출입문을 체계적으로 관리하여 방문객에게 깨끗한 인상을 줍니다."
  }
];

function PortfolioModal({ onClose }) {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Mock Data Fetching ---
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    // Simulate network delay
    const timer = setTimeout(() => {
      try {
        // In a real app, replace this with:
        // const response = await fetch('/api/naver-blog-posts'); // Your backend endpoint
        // if (!response.ok) throw new Error('Failed to fetch posts');
        // const data = await response.json();
        // setPosts(data);

        // Using Mock Data:
        setPosts(MOCK_POSTS);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching portfolio posts:", err);
        setError("시공 사례를 불러오는 중 오류가 발생했습니다.");
        setPosts([]);
        setIsLoading(false);
      }
    }, 500); // Simulate 0.5 second loading time

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, []); // Run once on mount


  // --- Background Scroll Lock ---
  useEffect(() => {
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    return () => {
      document.body.style.overflow = 'unset'; // Restore scroll on unmount
    };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={onClose} aria-label="닫기">&times;</button>
        <h2>최신 시공 사례</h2>

        <div className="portfolio-list">
          {isLoading && <p className="modal-loading-error-message">시공 사례를 불러오는 중...</p>}
          {error && <p className="modal-loading-error-message" style={{ color: 'red' }}>{error}</p>}
          {!isLoading && !error && posts.length === 0 && <p className="modal-loading-error-message">표시할 시공 사례가 없습니다.</p>}

          {!isLoading && !error && posts.map((post) => (
            <div key={post.id} className="portfolio-item">
              <h3>
                {/* Target="_blank" opens link in new tab, rel="noopener noreferrer" for security */}
                <a href={post.link} target="_blank" rel="noopener noreferrer">
                  {post.title}
                </a>
              </h3>
              <div className="post-meta">{post.date}</div>
              <p className="post-excerpt">{post.excerpt}</p>
              <a href={post.link} target="_blank" rel="noopener noreferrer" className="read-more-link">
                블로그에서 자세히 보기 &rarr;
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PortfolioModal;